import React, { useState, useEffect, useRef, useCallback } from 'react';
import RSSFeedDisplay from './RSSFeedDisplay';
import { BeamsBackground } from './components/ui/beams-background';
import { BlurIn } from './components/ui/blur-in';
import { Footer } from './components/ui/footer-section';
import Globe from './components/ui/globe';
import { Typewriter } from './components/ui/typewriter';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from './components/ui/select';
import './App.css';

// Bump this when replacing provider logo files in public/images.
const LOGO_ASSET_VERSION = '20260320';
const providerLogoUrl = (fileName) => `/images/${fileName}?v=${LOGO_ASSET_VERSION}`;

// Debounce hook for performance optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Each provider uses different naming in their feed text:
//   AWS  → "Asia Pacific (Singapore)", "ap-southeast-1"
//   GCP  → "asia-southeast1", "Singapore"
//   Azure→ "Southeast Asia", "Singapore"
// aws/gcp/azure are arrays — an item matches if ANY term is found.
// NO_REGION is a sentinel for providers with no presence in a given location.
const NO_REGION = ['zzz-no-region-match'];

const REGION_GROUPS = [
  // ── Asia Pacific ──────────────────────────────────────────────────────────
  // AWS: ap-southeast-1/2/3/4/5/7, ap-northeast-1/2/3, ap-south-1/2, ap-east-1
  // GCP: asia-southeast1/2, asia-northeast1/2/3, asia-south1/2, asia-east1/2,
  //      australia-southeast1/2
  // Azure: 18 regions across Southeast Asia, Japan, Korea, Australia, India, HK, TW, NZ
  {
    label: 'Asia Pacific',
    regions: [
      // ── Southeast Asia ──────────────────────────────────────────────
      {
        label: 'Singapore (ap-southeast-1)',
        aws:   ['Asia Pacific (Singapore)', 'ap-southeast-1', 'Singapore'],
        gcp:   ['asia-southeast1', 'Singapore'],
        azure: ['Southeast Asia', 'Singapore'],
      },
      {
        label: 'Bangkok (ap-southeast-7)',
        aws:   ['Asia Pacific (Thailand)', 'ap-southeast-7', 'Thailand', 'Bangkok'],
        gcp:   NO_REGION,
        azure: ['Thailand Central', 'Bangkok', 'Thailand'],
      },
      {
        label: 'Kuala Lumpur (ap-southeast-5)',
        aws:   ['Asia Pacific (Malaysia)', 'ap-southeast-5', 'Malaysia', 'Kuala Lumpur'],
        gcp:   NO_REGION,
        azure: ['Malaysia West', 'Kuala Lumpur', 'Malaysia'],
      },
      {
        label: 'Jakarta (ap-southeast-3)',
        aws:   ['Asia Pacific (Jakarta)', 'ap-southeast-3', 'Jakarta'],
        gcp:   ['asia-southeast2', 'Jakarta', 'Indonesia'],
        azure: ['Indonesia Central', 'Jakarta'],
      },
      // ── Japan ───────────────────────────────────────────────────────
      {
        label: 'Tokyo (ap-northeast-1)',
        aws:   ['Asia Pacific (Tokyo)', 'ap-northeast-1', 'Tokyo'],
        gcp:   ['asia-northeast1', 'Tokyo', 'Japan'],
        azure: ['Japan East', 'Tokyo', 'Japan'],
      },
      {
        label: 'Osaka (ap-northeast-3)',
        aws:   ['Asia Pacific (Osaka)', 'ap-northeast-3', 'Osaka'],
        gcp:   ['asia-northeast2', 'Osaka'],
        azure: ['Japan West', 'Osaka'],
      },
      // ── Korea ───────────────────────────────────────────────────────
      {
        label: 'Seoul (ap-northeast-2)',
        aws:   ['Asia Pacific (Seoul)', 'ap-northeast-2', 'Seoul'],
        gcp:   ['asia-northeast3', 'Seoul', 'Korea'],
        azure: ['Korea Central', 'Korea South', 'Seoul', 'Busan', 'Korea'],
      },
      // ── Australia & New Zealand ─────────────────────────────────────
      {
        label: 'Sydney (ap-southeast-2)',
        aws:   ['Asia Pacific (Sydney)', 'ap-southeast-2', 'Sydney'],
        gcp:   ['australia-southeast1', 'Sydney', 'Australia'],
        azure: ['Australia East', 'Sydney', 'Australia'],
      },
      {
        label: 'Melbourne (ap-southeast-4)',
        aws:   ['Asia Pacific (Melbourne)', 'ap-southeast-4', 'Melbourne'],
        gcp:   ['australia-southeast2', 'Melbourne'],
        azure: ['Australia Southeast', 'Melbourne'],
      },
      {
        label: 'Canberra',
        aws:   NO_REGION,
        gcp:   NO_REGION,
        azure: ['Australia Central', 'Canberra'],
      },
      {
        label: 'Auckland',
        aws:   NO_REGION,
        gcp:   NO_REGION,
        azure: ['New Zealand North', 'Auckland', 'New Zealand'],
      },
      // ── India ───────────────────────────────────────────────────────
      {
        label: 'Mumbai (ap-south-1)',
        aws:   ['Asia Pacific (Mumbai)', 'ap-south-1', 'Mumbai', 'India'],
        gcp:   ['asia-south1', 'Mumbai'],
        azure: ['West India', 'Mumbai'],
      },
      {
        label: 'Hyderabad / Chennai (ap-south-2)',
        aws:   ['Asia Pacific (Hyderabad)', 'ap-south-2', 'Hyderabad'],
        gcp:   NO_REGION,
        azure: ['South India', 'Chennai', 'Hyderabad'],
      },
      {
        label: 'Delhi / Pune',
        aws:   NO_REGION,
        gcp:   ['asia-south2', 'Delhi'],
        azure: ['Central India', 'Pune', 'Delhi'],
      },
      // ── East Asia ───────────────────────────────────────────────────
      {
        label: 'Hong Kong (ap-east-1)',
        aws:   ['Asia Pacific (Hong Kong)', 'ap-east-1', 'Hong Kong'],
        gcp:   ['asia-east2', 'Hong Kong'],
        azure: ['East Asia', 'Hong Kong'],
      },
      {
        label: 'Taiwan / Taipei',
        aws:   NO_REGION,
        gcp:   ['asia-east1', 'Taiwan', 'Taipei'],
        azure: ['Taiwan North', 'Taiwan Northwest', 'Taipei', 'Taiwan'],
      },
    ],
  },

  // ── Americas ──────────────────────────────────────────────────────────────
  // AWS: us-east-1/2, us-west-1/2, ca-central-1, ca-west-1, sa-east-1, mx-central-1
  // GCP: us-east1/4/5, us-west1/2/3/4, us-central1, us-south1,
  //      northamerica-northeast1/2, northamerica-south1, southamerica-east1/west1
  // Azure: 10 US regions, Canada Central/East, Mexico Central, Brazil South/SE
  {
    label: 'Americas',
    regions: [
      // ── US East ─────────────────────────────────────────────────────
      {
        label: 'N. Virginia (us-east-1)',
        aws:   ['US East (N. Virginia)', 'us-east-1', 'N. Virginia', 'Virginia'],
        gcp:   ['us-east4', 'Northern Virginia', 'Virginia'],
        azure: ['East US', 'East US 2', 'Virginia'],
      },
      {
        label: 'Ohio (us-east-2)',
        aws:   ['US East (Ohio)', 'us-east-2', 'Ohio'],
        gcp:   ['us-east5', 'Ohio', 'Columbus'],
        azure: NO_REGION,
      },
      {
        label: 'Southeast US',
        aws:   NO_REGION,
        gcp:   ['us-east1', 'South Carolina', 'Moncks Corner'],
        azure: ['East US 3', 'Georgia', 'Atlanta'],
      },
      // ── US West ─────────────────────────────────────────────────────
      {
        label: 'N. California (us-west-1)',
        aws:   ['US West (N. California)', 'us-west-1', 'N. California', 'California'],
        gcp:   ['us-west2', 'Los Angeles', 'California'],
        azure: ['West US', 'California'],
      },
      {
        label: 'Oregon (us-west-2)',
        aws:   ['US West (Oregon)', 'us-west-2', 'Oregon'],
        gcp:   ['us-west1', 'Oregon', 'The Dalles'],
        azure: ['West US 2', 'West US 3', 'Oregon', 'Washington', 'Arizona'],
      },
      {
        label: 'US Mountain West',
        aws:   NO_REGION,
        gcp:   ['us-west3', 'us-west4', 'Salt Lake City', 'Las Vegas', 'Utah', 'Nevada'],
        azure: ['West Central US', 'Wyoming'],
      },
      // ── US Central & South ──────────────────────────────────────────
      {
        label: 'Iowa / Central US',
        aws:   NO_REGION,
        gcp:   ['us-central1', 'Iowa'],
        azure: ['Central US', 'Iowa'],
      },
      {
        label: 'Texas / South Central US',
        aws:   NO_REGION,
        gcp:   ['us-south1', 'Texas', 'Dallas'],
        azure: ['South Central US', 'North Central US', 'Texas', 'San Antonio', 'Illinois', 'Chicago'],
      },
      // ── Canada ──────────────────────────────────────────────────────
      {
        label: 'Toronto (ca-central-1)',
        aws:   ['Canada (Central)', 'ca-central-1', 'Toronto', 'Canada'],
        gcp:   ['northamerica-northeast1', 'northamerica-northeast2', 'Montreal', 'Quebec', 'Canada'],
        azure: ['Canada Central', 'Canada East', 'Toronto', 'Quebec City', 'Canada'],
      },
      {
        label: 'Calgary (ca-west-1)',
        aws:   ['Canada West', 'ca-west-1', 'Calgary'],
        gcp:   NO_REGION,
        azure: NO_REGION,
      },
      // ── Mexico ──────────────────────────────────────────────────────
      {
        label: 'Mexico',
        aws:   ['Mexico', 'mx-central-1', 'Mexico City'],
        gcp:   ['northamerica-south1', 'Mexico', 'Querétaro', 'Queretaro'],
        azure: ['Mexico Central', 'Mexico', 'Querétaro', 'Queretaro'],
      },
      // ── South America ────────────────────────────────────────────────
      {
        label: 'São Paulo (sa-east-1)',
        aws:   ['South America (São Paulo)', 'sa-east-1', 'São Paulo', 'Sao Paulo', 'Brazil'],
        gcp:   ['southamerica-east1', 'São Paulo', 'Sao Paulo', 'Brazil'],
        azure: ['Brazil South', 'Brazil Southeast', 'São Paulo', 'Sao Paulo', 'Brazil'],
      },
      {
        label: 'Santiago / Chile',
        aws:   NO_REGION,
        gcp:   ['southamerica-west1', 'Chile', 'Santiago'],
        azure: NO_REGION,
      },
    ],
  },

  // ── Europe ────────────────────────────────────────────────────────────────
  // AWS: eu-west-1/2/3, eu-central-1/2, eu-north-1, eu-south-1/2
  // GCP: europe-west1/2/3/4/6/8/9/10/12, europe-north1, europe-central2,
  //      europe-southwest1
  // Azure: 17 regions across Western, Central, Northern & Southern Europe
  {
    label: 'Europe',
    regions: [
      // ── British Isles ────────────────────────────────────────────────
      {
        label: 'Ireland (eu-west-1)',
        aws:   ['Europe (Ireland)', 'eu-west-1', 'Ireland'],
        gcp:   ['europe-west1', 'Belgium', 'Ireland'],
        azure: ['North Europe', 'Ireland'],
      },
      {
        label: 'London (eu-west-2)',
        aws:   ['Europe (London)', 'eu-west-2', 'London'],
        gcp:   ['europe-west2', 'London', 'UK'],
        azure: ['UK South', 'UK West', 'London', 'Cardiff', 'Wales'],
      },
      // ── Western Europe ──────────────────────────────────────────────
      {
        label: 'Netherlands',
        aws:   NO_REGION,
        gcp:   ['europe-west4', 'Netherlands', 'Amsterdam', 'Eemshaven'],
        azure: ['West Europe', 'Netherlands', 'Amsterdam'],
      },
      {
        label: 'Paris (eu-west-3)',
        aws:   ['Europe (Paris)', 'eu-west-3', 'Paris', 'France'],
        gcp:   ['europe-west9', 'Paris', 'France'],
        azure: ['France Central', 'France South', 'Paris', 'Marseille', 'France'],
      },
      // ── Central Europe ──────────────────────────────────────────────
      {
        label: 'Frankfurt (eu-central-1)',
        aws:   ['Europe (Frankfurt)', 'eu-central-1', 'Frankfurt', 'Germany'],
        gcp:   ['europe-west3', 'europe-west10', 'Frankfurt', 'Berlin', 'Germany'],
        azure: ['Germany West Central', 'Germany North', 'Frankfurt', 'Berlin', 'Germany'],
      },
      {
        label: 'Zurich (eu-central-2)',
        aws:   ['Europe (Zurich)', 'eu-central-2', 'Zurich', 'Switzerland'],
        gcp:   ['europe-west6', 'Zurich', 'Switzerland'],
        azure: ['Switzerland North', 'Switzerland West', 'Zurich', 'Geneva', 'Switzerland'],
      },
      {
        label: 'Warsaw',
        aws:   NO_REGION,
        gcp:   ['europe-central2', 'Warsaw', 'Poland'],
        azure: ['Poland Central', 'Warsaw', 'Poland'],
      },
      // ── Southern Europe ─────────────────────────────────────────────
      {
        label: 'Milan (eu-south-1)',
        aws:   ['Europe (Milan)', 'eu-south-1', 'Milan', 'Italy'],
        gcp:   ['europe-west8', 'europe-west12', 'Milan', 'Turin', 'Italy'],
        azure: ['Italy North', 'Milan', 'Turin', 'Italy'],
      },
      {
        label: 'Madrid (eu-south-2)',
        aws:   ['Europe (Spain)', 'eu-south-2', 'Madrid', 'Spain'],
        gcp:   ['europe-southwest1', 'Madrid', 'Spain'],
        azure: ['Spain Central', 'Madrid', 'Spain'],
      },
      {
        label: 'Athens',
        aws:   NO_REGION,
        gcp:   NO_REGION,
        azure: ['Greece Central', 'Athens', 'Greece'],
      },
      // ── Northern Europe ─────────────────────────────────────────────
      {
        label: 'Stockholm (eu-north-1)',
        aws:   ['Europe (Stockholm)', 'eu-north-1', 'Stockholm', 'Sweden'],
        gcp:   ['europe-north1', 'Finland', 'Helsinki', 'Hamina'],
        azure: ['Sweden Central', 'Sweden South', 'Stockholm', 'Gävle', 'Malmö', 'Sweden'],
      },
      {
        label: 'Oslo',
        aws:   NO_REGION,
        gcp:   NO_REGION,
        azure: ['Norway East', 'Norway West', 'Oslo', 'Stavanger', 'Norway'],
      },
    ],
  },

  // ── Middle East & Africa ──────────────────────────────────────────────────
  // AWS: me-south-1, me-central-1, il-central-1, af-south-1
  // GCP: me-central1, me-central2, me-west1, africa-south1
  // Azure: UAE North/Central, Qatar Central, Israel Central, Saudi Arabia North,
  //        South Africa North/West
  {
    label: 'Middle East & Africa',
    regions: [
      {
        label: 'UAE / Dubai (me-central-1)',
        aws:   ['Middle East (UAE)', 'me-central-1', 'UAE', 'Dubai'],
        gcp:   NO_REGION,
        azure: ['UAE North', 'UAE Central', 'Dubai', 'Abu Dhabi', 'UAE'],
      },
      {
        label: 'Saudi Arabia',
        aws:   NO_REGION,
        gcp:   ['me-central2', 'Saudi Arabia', 'Dammam'],
        azure: ['Saudi Arabia North', 'Saudi Arabia West', 'Riyadh', 'Saudi Arabia'],
      },
      {
        label: 'Qatar / Doha',
        aws:   NO_REGION,
        gcp:   ['me-central1', 'Qatar', 'Doha'],
        azure: ['Qatar Central', 'Qatar', 'Doha'],
      },
      {
        label: 'Bahrain (me-south-1)',
        aws:   ['Middle East (Bahrain)', 'me-south-1', 'Bahrain'],
        gcp:   NO_REGION,
        azure: NO_REGION,
      },
      {
        label: 'Israel / Tel Aviv (il-central-1)',
        aws:   ['Israel (Tel Aviv)', 'il-central-1', 'Israel', 'Tel Aviv'],
        gcp:   ['me-west1', 'Israel', 'Tel Aviv'],
        azure: ['Israel Central', 'Israel', 'Tel Aviv'],
      },
      {
        label: 'South Africa',
        aws:   ['Africa (Cape Town)', 'af-south-1', 'Cape Town', 'South Africa'],
        gcp:   ['africa-south1', 'Johannesburg', 'South Africa'],
        azure: ['South Africa North', 'South Africa West', 'Johannesburg', 'Cape Town', 'South Africa'],
      },
    ],
  },
];

// Flat list used for state matching (includes All Regions sentinel at index 0)
const REGIONS = [
  { label: 'All Regions', aws: [], gcp: [], azure: [] },
  ...REGION_GROUPS.flatMap(g => g.regions),
];

const App = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(REGIONS[0]);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const searchInputRef = useRef(null);

  // Debounce search query to reduce re-renders (150ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 150);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Focus search on '/' key
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      // Show keyboard help on '?' key
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowKeyboardHelp(!showKeyboardHelp);
        return;
      }

      // Navigate items with j/k keys
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        const items = document.querySelectorAll('.feed-item[data-item-link]');
        if (items.length === 0) return;

        const currentIndex = Array.from(items).findIndex(item => 
          item === document.activeElement || item.contains(document.activeElement)
        );

        let nextIndex;
        if (e.key === 'j') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }

        items[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        items[nextIndex]?.querySelector('.feed-item-title')?.focus();
      }

      // Open item with 'o' key
      if (e.key === 'o') {
        const focusedItem = document.activeElement.closest('.feed-item');
        if (focusedItem) {
          const link = focusedItem.querySelector('.feed-item-title');
          if (link) {
            link.click();
          }
        }
      }

      // Clear search with Escape
      if (e.key === 'Escape' && searchQuery) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [searchQuery, showKeyboardHelp]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  return (
    <BeamsBackground intensity="strong">
      <div className="app">
       <header className="app-header">
          <div className="header-content">
            <div className="left-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <BlurIn word="Cloud Service Provider RSS Feeds" duration={0.8} />
                <Globe size={72} />
              </div>
              <p className="subheading">
                Learn about announcements, launches, news, innovation and more on
              </p>
              <div className="subheading-typewriter">
                <Typewriter
                  text={[
                    'Amazon Web Services',
                    'Google Cloud',
                    'Microsoft Azure',
                    'the cloud',
                    'things that make the world a better place',
                  ]}
                  speed={70}
                  className="text-yellow-400"
                  waitTime={1500}
                  deleteSpeed={40}
                  cursorChar="_"
                />
              </div>
            </div>
            <div className="right-section">
              <div className="search-container">
                <div className="search-input-wrapper">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search feeds... (Press / to focus)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <Select
                  value={selectedRegion.label === 'All Regions' ? '' : selectedRegion.label}
                  onValueChange={(val) => {
                    const region = val ? REGIONS.find(r => r.label === val) : REGIONS[0];
                    setSelectedRegion(region || REGIONS[0]);
                  }}
                >
                  <SelectTrigger placeholder="All Regions" />
                  <SelectContent>
                    <SelectItem value="">All Regions</SelectItem>
                    {REGION_GROUPS.map((group) => (
                      <React.Fragment key={group.label}>
                        <SelectSeparator />
                        <SelectGroup>
                          <SelectLabel>{group.label}</SelectLabel>
                          {group.regions.map(r => (
                            <SelectItem key={r.label} value={r.label}>{r.label}</SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </header>

        {showKeyboardHelp && (
          <div className="keyboard-help-modal" onClick={() => setShowKeyboardHelp(false)}>
            <div className="keyboard-help-content" onClick={(e) => e.stopPropagation()}>
              <h2>Keyboard Shortcuts</h2>
              <div className="keyboard-shortcuts-list">
                <div className="shortcut-item">
                  <kbd>/</kbd>
                  <span>Focus search</span>
                </div>
                <div className="shortcut-item">
                  <kbd>j</kbd>
                  <span>Next item</span>
                </div>
                <div className="shortcut-item">
                  <kbd>k</kbd>
                  <span>Previous item</span>
                </div>
                <div className="shortcut-item">
                  <kbd>o</kbd>
                  <span>Open selected item</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Esc</kbd>
                  <span>Clear search / Close</span>
                </div>
                <div className="shortcut-item">
                  <kbd>?</kbd>
                  <span>Show/hide this help</span>
                </div>
              </div>
              <button className="close-help" onClick={() => setShowKeyboardHelp(false)}>
                Close
              </button>
            </div>
          </div>
        )}

      <div className="feed-disclaimer">
        All available items are shown per provider &mdash; coverage depth depends on each provider's RSS feed.
        The date range shown in each card header reflects actual feed history.
      </div>

      {/* Mobile-only provider filter tabs */}
      <div className="provider-tabs">
        {[
          { id: 'all',    label: 'All' },
          { id: 'aws',    label: 'AWS' },
          { id: 'gcp',    label: 'Google' },
          { id: 'azure',  label: 'Azure' },
        ].map(({ id, label }) => (
          <button
            key={id}
            className={`provider-tab provider-tab--${id}${selectedProvider === id ? ' provider-tab--active' : ''}`}
            onClick={() => setSelectedProvider(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="feed-container">
        <div className="provider-card aws" style={selectedProvider !== 'all' && selectedProvider !== 'aws' ? { display: 'none' } : {}}>
          <RSSFeedDisplay
            feedUrl="https://aws.amazon.com/about-aws/whats-new/recent/feed/"
            title="Amazon Web Services"
            provider="aws"
            logoUrl={providerLogoUrl('aws-logo.png')}
            searchQuery={debouncedSearchQuery}
            regionQuery={selectedRegion.aws}
          />
        </div>
        <div className="provider-card google" style={selectedProvider !== 'all' && selectedProvider !== 'gcp' ? { display: 'none' } : {}}>
          <RSSFeedDisplay
            feedUrl="https://cloudblog.withgoogle.com/rss/"
            title="Google Cloud"
            provider="google"
            logoUrl={providerLogoUrl('google-cloud-logo.png')}
            searchQuery={debouncedSearchQuery}
            regionQuery={selectedRegion.gcp}
          />
        </div>
        <div className="provider-card azure" style={selectedProvider !== 'all' && selectedProvider !== 'azure' ? { display: 'none' } : {}}>
          <RSSFeedDisplay
            feedUrl="https://www.microsoft.com/releasecommunications/api/v2/azure/rss"
            title="Microsoft Azure"
            provider="azure"
            logoUrl={providerLogoUrl('azure-logo.png')}
            searchQuery={debouncedSearchQuery}
            regionQuery={selectedRegion.azure}
          />
        </div>
      </div>
      <Footer />
      </div>
    </BeamsBackground>
  );
};

export default App;